// UserMenu — avatar đáy sidebar 80px, bấm ra popup "cá nhân".
//
// Vì sao tồn tại: mặc định SidebarPrimary cho avatar navigate THẲNG sang /me, nên muốn
// đổi ngôn ngữ người dùng phải: bấm avatar → vào /me → chọn tab "Giao diện" → mới thấy
// bộ chọn. Người đọc không nổi tiếng Việt thì 3 bước đó đều là chữ Việt. Popup này đưa
// bộ chọn ngôn ngữ lên NGAY chỗ bấm đầu tiên (1 cú bấm), giữ nguyên khối đầy đủ ở /me.
//
// Dùng qua slot, KHÔNG sửa default của SidebarPrimary:
//   <AppLayout primaryBottomSlot={<UserMenu />} … />
// Host nào không pass thì vẫn là avatar-link như cũ (Core giữ user menu modal riêng).
//
// Đổi ngôn ngữ = LanguageSwitcher (đã tự lưu localStorage) + ghi hồ sơ server rồi
// RELOAD — giống AppearanceSection ở /me: nhãn đã dịch nằm trong useMemo rải khắp app,
// reload rẻ hơn và chắc hơn là đi vá deps từng chỗ.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Settings, Globe, Palette, Sun, Moon, Monitor } from 'lucide-react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useAuth } from '@host/auth';
import LanguageSwitcher from '../../../i18n/LanguageSwitcher.jsx';
import { getEnabledLocales } from '../../../i18n/runtime.js';
import useThemeMode from '../../../hooks/useThemeMode';

export default function UserMenu() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { user, logout, changeLocale } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Đóng khi bấm ra ngoài / bấm Esc. mousedown (không phải click) để popup đóng trước
  // khi phần tử dưới nhận click — tránh bấm 1 lần mà vừa đóng vừa kích hoạt menu khác.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const name = user?.name || user?.display_name || user?.email || 'User';
  const initial = (name[0] || 'U').toUpperCase();
  const multiLocale = getEnabledLocales().length > 1;
  const [themeMode, setThemeMode] = useThemeMode();
  // Nhãn phải nằm TRONG component để đi qua macro `t` — hằng ngoài component thì cần
  // msg`` + i18n._() ở mọi chỗ đọc, quên một chỗ là lòi msgid thô ra màn.
  const THEME_CHOICES = [
    { key: 'light', Icon: Sun, label: t`Sáng` },
    { key: 'dark', Icon: Moon, label: t`Tối` },
    { key: 'auto', Icon: Monitor, label: t`Theo máy` },
  ];

  const go = (to) => { setOpen(false); navigate(to); };

  const handleLogout = async () => {
    setOpen(false);
    // Ngắt chat realtime TRƯỚC logout — y hệt AccountSection ở /me: SPA nav không reload
    // page nên watchdog chatStomp sẽ chạy mồ côi (xin /chat/jwt với phiên đã chết) và
    // người login sau kế thừa chat-JWT của người trước (chatApi cache 1h).
    // Dynamic import để chunk chat không bị kéo vào bundle chính.
    try {
      const [stomp, chatApi] = await Promise.all([
        import('../../../pages/team-chat/lib/chatStomp'),
        import('../../../pages/team-chat/lib/chatApi'),
      ]);
      stomp.disconnect();
      chatApi.resetChatSession();
    } catch { /* chat chưa từng load hoặc lỗi chunk — bỏ qua, logout vẫn tiếp tục */ }
    await logout();
    navigate('/login');
  };

  return (
    <div className="p-2 border-t border-slate-800/80 shrink-0">
      <div ref={wrapRef} className="relative w-fit mx-auto">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={open}
          className="flex items-center justify-center rounded-full hover:opacity-80 transition"
          title={t`${name} — Cài đặt cá nhân`}
        >
          {user?.avatar ? (
            <img src={user.avatar} alt={name} className="w-10 h-10 rounded-full object-cover ring-2 ring-white/10" />
          ) : (
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-sm font-bold shadow-md ring-2 ring-white/10">
              {initial}
            </div>
          )}
        </button>

        {open && (
          // z-[60]: popup nằm TRONG SidebarPrimary (DOM trước SidebarSecondary) nên không
          // có z cao hơn là bị panel 240px phủ lên.
          <div
            role="menu"
            className="absolute bottom-0 left-[52px] z-[60] w-60 bg-white border border-slate-200 rounded-xl shadow-2xl py-1 text-slate-700"
          >
            <div className="px-3 py-2 border-b border-slate-100">
              <p className="text-sm font-semibold text-slate-900 truncate">{name}</p>
              {user?.email ? <p className="text-[11.5px] text-slate-500 truncate">{user.email}</p> : null}
            </div>

            {multiLocale && (
              <div className="border-b border-slate-100 py-1">
                <p className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1">
                  <Globe className="w-3 h-3" /> <Trans>Ngôn ngữ</Trans>
                </p>
                <LanguageSwitcher
                  variant="menu"
                  onChange={async (code) => {
                    // LanguageSwitcher đã đổi ngôn ngữ TẠI CHỖ (kernel nạp catalog mọi cụm rồi
                    // activate) và I18nProvider dựng lại cây con — nên KHÔNG tải lại trang nữa.
                    // Ở đây chỉ ghi meta.users.locale để thiết bị khác + BE soạn chữ đúng
                    // ngôn ngữ; ghi hụt cũng không sao, lựa chọn đã lưu ở máy.
                    try { await changeLocale?.(code); } catch { /* không chặn việc đổi */ }
                  }}
                />
              </div>
            )}

            {/* Sáng / Tối / Theo máy — đặt ngay đây theo yêu cầu sếp Phú 15/08: trước đó đổi
                theme phải đi 3 bước (avatar → /me → tab Giao diện). Khối đầy đủ (kèm mô tả
                từng chế độ) vẫn giữ nguyên ở /me; hai nơi dùng CHUNG hook useThemeMode nên
                bấm ở đây thì /me cũng hiện đúng, không lệch. */}
            <div className="border-b border-slate-100 py-1">
              <p className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1">
                <Palette className="w-3 h-3" /> <Trans>Giao diện</Trans>
              </p>
              <div className="px-2 pb-1 grid grid-cols-3 gap-1">
                {THEME_CHOICES.map(({ key, Icon, label }) => {
                  const active = themeMode === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      role="menuitemradio"
                      aria-checked={active}
                      onClick={() => setThemeMode(key)}
                      title={label}
                      className={`flex flex-col items-center gap-0.5 py-1.5 rounded-lg border text-[10.5px] transition ${
                        active
                          ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      <Icon size={14} className={active ? 'text-blue-600' : 'text-slate-400'} />
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              role="menuitem"
              onClick={() => go('/me')}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50"
            >
              <Settings size={15} className="text-slate-400" /> <Trans>Cài đặt cá nhân</Trans>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <LogOut size={15} /> <Trans>Đăng xuất</Trans>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
